import { Button } from "../ui/button";
import {
  ArrowRight, CheckCircle2, Award, FileText, Sparkles,
  Users, ShieldAlert, Database, Building2, Calendar,
  ClipboardList, Activity,
} from "lucide-react";

export default function ServiceAsSoftwareSection({ onGetStarted }) {
  return (
    <section className="py-24 sm:py-32 px-4 sm:px-6 lg:px-8 bg-slate-900 overflow-hidden" data-testid="service-as-software-section">
      <div className="max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center">
          {/* Left Column - Text Content */}
          <div className="order-2 lg:order-1">
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white tracking-tight mb-4 sm:mb-6 leading-tight">
              Stop Building Your Resiliency Plans.{" "}
              <span className="bg-gradient-to-r from-emerald-400 to-emerald-300 bg-clip-text text-transparent">
                Start Having Them Built.
              </span>
            </h2>
            
            <p className="text-base sm:text-lg lg:text-xl text-emerald-400 font-semibold mb-4 sm:mb-6">
              The world's first 'Service-as-Software' BCM platform. We don't sell you a database and a bill—we deliver ISO-compliant autonomy.
            </p>
            
            <p className="text-slate-300 leading-relaxed mb-6 sm:mb-8 text-sm sm:text-base lg:text-lg">
              For decades, Business Continuity Management has been broken. Traditional software is an 'Empty Vessel' that creates a second job for your team. We changed the math. By shifting from Software-as-a-Service to <strong className="text-white">World's first Service-as-Software</strong>, our AI agent autonomously interviews process owners, infers risks, and writes your plans. Your BCM Manager stops being a data-entry clerk and starts being a strategist.
            </p>
            
            <Button 
              onClick={onGetStarted}
              className="w-full sm:w-auto bg-emerald-500 hover:bg-emerald-600 text-white font-semibold px-6 sm:px-8 py-4 sm:py-6 text-base sm:text-lg rounded-xl shadow-lg shadow-emerald-500/30 transition-all hover:shadow-emerald-500/50 hover:scale-105"
              data-testid="autonomous-resilience-btn"
            >
              Experience Autonomous Resilience
              <ArrowRight className="ml-2 w-4 h-4 sm:w-5 sm:h-5" />
            </Button>
          </div>
          
          {/* Right Column - Custom CSS/SVG Illustration (replaces legacy GBRMS image) */}
          <div className="order-1 lg:order-2 relative">
            <div className="absolute -inset-4 bg-emerald-500/20 rounded-3xl blur-3xl"></div>
            <div
              className="relative rounded-2xl overflow-hidden shadow-2xl shadow-emerald-500/20 border border-slate-700 bg-gradient-to-br from-slate-900 via-slate-950 to-slate-900"
              style={{ aspectRatio: "5 / 4", minHeight: "440px" }}
              data-testid="service-as-software-illustration"
            >
              {/* Subtle grid background */}
              <svg className="absolute inset-0 w-full h-full opacity-20" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <defs>
                  <pattern id="grid-saa" width="36" height="36" patternUnits="userSpaceOnUse">
                    <path d="M 36 0 L 0 0 0 36" fill="none" stroke="rgba(16,185,129,0.25)" strokeWidth="0.6" />
                  </pattern>
                </defs>
                <rect width="100%" height="100%" fill="url(#grid-saa)" />
              </svg>

              {/* Top center: Brand wordmark */}
              <div className="absolute pointer-events-none" style={{ top: '5%', left: '50%', transform: 'translateX(-50%)' }} data-testid="trust-tech-wordmark-overlay">
                <div className="text-center">
                  <div className="font-extrabold tracking-tight leading-none" style={{ fontSize: "clamp(15px, 2vw, 26px)", color: "#fff", textShadow: "0 2px 10px rgba(0,0,0,0.6)" }}>
                    Resiliency<span style={{ color: "#00BCD4" }}>HUB</span>
                  </div>
                  <div className="font-bold tracking-[0.25em] leading-none mt-1" style={{ fontSize: "clamp(8px, 0.9vw, 11px)", color: "#10b981" }}>
                    SERVICE-AS-SOFTWARE
                  </div>
                </div>
              </div>

              {/* Brand badge (top-left) */}
              <div className="absolute top-3 left-3 bg-slate-800/80 border border-slate-700 rounded-lg px-2 py-1 flex items-center gap-1.5 shadow-lg backdrop-blur-sm">
                <img
                  src="https://customer-assets.emergentagent.com/job_cc74fbba-7d42-4e61-a0ea-eedd771fa683/artifacts/7xq5yifk_ResHubNEW1.1-removebg-preview.png"
                  alt="ResiliencyHUB"
                  className="h-4 w-auto"
                />
                <span className="text-[9px] font-extrabold text-white tracking-tight whitespace-nowrap">Resiliency<span style={{ color: "#00BCD4" }}>HUB</span></span>
              </div>

              {/* LEFT SIDE — Input Data Sources */}
              <div className="absolute left-[3%] top-[22%] bottom-[24%] w-[26%] flex flex-col justify-between gap-2">
                <InputPill icon={Users}        label="Process Owners" sub="Interviewed" color="emerald" delay="0s"   />
                <InputPill icon={Database}     label="Asset Registry" sub="48 assets"   color="sky"     delay="0.4s" />
                <InputPill icon={ShieldAlert}  label="Risk Inputs"    sub="22 risks"    color="amber"   delay="0.8s" />
                <InputPill icon={Building2}    label="Org Structure"  sub="6 depts"     color="cyan"    delay="1.2s" />
              </div>

              {/* CENTER — AI Core with orbiting capability rings */}
              <div className="absolute left-1/2 top-[50%] -translate-x-1/2 -translate-y-1/2 w-[28%] aspect-square flex items-center justify-center">
                {/* Outer dashed ring */}
                <div className="absolute inset-0 rounded-full border border-emerald-500/30 border-dashed" style={{ animation: "spin 22s linear infinite" }} />
                {/* Inner solid ring */}
                <div className="absolute inset-[14%] rounded-full border border-emerald-400/30" style={{ animation: "spinReverse 16s linear infinite" }}>
                  {/* Orbiting capability dots */}
                  <div className="absolute -top-1.5 left-1/2 -translate-x-1/2 w-3 h-3 rounded-full bg-emerald-400 shadow-[0_0_10px_rgba(52,211,153,0.8)]" />
                  <div className="absolute top-1/2 -right-1.5 -translate-y-1/2 w-2.5 h-2.5 rounded-full bg-cyan-400 shadow-[0_0_10px_rgba(34,211,238,0.8)]" />
                  <div className="absolute -bottom-1.5 left-1/2 -translate-x-1/2 w-3 h-3 rounded-full bg-sky-400 shadow-[0_0_10px_rgba(56,189,248,0.8)]" />
                  <div className="absolute top-1/2 -left-1.5 -translate-y-1/2 w-2.5 h-2.5 rounded-full bg-emerald-300 shadow-[0_0_10px_rgba(110,231,183,0.8)]" />
                </div>
                {/* Core orb */}
                <div className="relative w-[58%] aspect-square rounded-full flex flex-col items-center justify-center" style={{ background: "radial-gradient(circle at 30% 30%, #34d399, #047857 60%, #064e3b 100%)", boxShadow: "0 0 60px rgba(16,185,129,0.55), inset 0 0 24px rgba(255,255,255,0.18)", animation: "pulseGlow 3s ease-in-out infinite" }}>
                  <Sparkles className="w-7 h-7 sm:w-8 sm:h-8 text-white drop-shadow-[0_2px_6px_rgba(0,0,0,0.4)]" />
                  <span className="text-[9px] sm:text-[10px] font-extrabold tracking-[0.18em] text-white uppercase mt-1">AI Core</span>
                </div>
                {/* Capability tags around orb */}
                <span className="absolute -top-1 -left-2 bg-slate-800/95 border border-emerald-500/40 rounded px-1.5 py-0.5 text-[8.5px] font-bold text-emerald-300 tracking-wide whitespace-nowrap shadow-md">BIA</span>
                <span className="absolute -top-1 -right-2 bg-slate-800/95 border border-cyan-500/40 rounded px-1.5 py-0.5 text-[8.5px] font-bold text-cyan-300 tracking-wide whitespace-nowrap shadow-md">RA</span>
                <span className="absolute -bottom-1 -left-2 bg-slate-800/95 border border-sky-500/40 rounded px-1.5 py-0.5 text-[8.5px] font-bold text-sky-300 tracking-wide whitespace-nowrap shadow-md">PLAN</span>
                <span className="absolute -bottom-1 -right-2 bg-slate-800/95 border border-emerald-400/40 rounded px-1.5 py-0.5 text-[8.5px] font-bold text-emerald-200 tracking-wide whitespace-nowrap shadow-md">EXERCISE</span>
              </div>

              {/* Animated flow lines: inputs → AI core → outputs */}
              <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 500 400" preserveAspectRatio="none" aria-hidden="true">
                <defs>
                  <linearGradient id="flow-in" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#10b981" stopOpacity="0.05" />
                    <stop offset="60%" stopColor="#10b981" stopOpacity="0.7" />
                    <stop offset="100%" stopColor="#10b981" stopOpacity="0.95" />
                  </linearGradient>
                  <linearGradient id="flow-out" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#00BCD4" stopOpacity="0.95" />
                    <stop offset="40%" stopColor="#00BCD4" stopOpacity="0.7" />
                    <stop offset="100%" stopColor="#00BCD4" stopOpacity="0.05" />
                  </linearGradient>
                </defs>
                {/* Inputs → AI core */}
                {[120, 175, 230, 285].map((y, i) => (
                  <path key={`in-${i}`} d={`M 145 ${y} Q 200 ${(y + 200) / 2} 250 200`} stroke="url(#flow-in)" strokeWidth="1.5" fill="none" strokeDasharray="4 4">
                    <animate attributeName="stroke-dashoffset" from="0" to="-32" dur={`${2.4 + i * 0.3}s`} repeatCount="indefinite" />
                  </path>
                ))}
                {/* AI core → Outputs */}
                {[140, 195, 250].map((y, i) => (
                  <path key={`out-${i}`} d={`M 250 200 Q 320 ${(200 + y) / 2} 380 ${y}`} stroke="url(#flow-out)" strokeWidth="2" fill="none" strokeDasharray="3 5">
                    <animate attributeName="stroke-dashoffset" from="0" to="-32" dur={`${2.6 + i * 0.4}s`} repeatCount="indefinite" />
                  </path>
                ))}
              </svg>

              {/* RIGHT SIDE — Output artifacts */}
              <div className="absolute right-[3%] top-[22%] bottom-[24%] w-[26%] flex flex-col justify-between gap-2">
                <OutputCard icon={ClipboardList} label="BIA Report"      meta="ISO 22301 §8.2" delay="0s"   />
                <OutputCard icon={ShieldAlert}   label="Risk Register"   meta="ISO 22301 §8.3" delay="0.3s" />
                <OutputCard icon={FileText}      label="Recovery Plan"   meta="ISO 22301 §8.4" delay="0.6s" certified />
                <OutputCard icon={Calendar}      label="Exercise Sched." meta="ISO 22301 §8.5" delay="0.9s" />
              </div>

              {/* BOTTOM — Live KPI strip */}
              <div className="absolute bottom-3 left-3 right-3 bg-slate-800/85 border border-slate-700 rounded-lg px-3 py-2 flex items-center justify-between gap-2 shadow-lg backdrop-blur-md" data-testid="live-kpi-strip">
                <div className="flex items-center gap-1.5">
                  <Activity className="w-3 h-3 text-emerald-400 animate-pulse" />
                  <span className="text-[9px] font-bold text-emerald-300 tracking-wide uppercase">Live</span>
                </div>
                <KpiInline label="Plans" value="127" />
                <KpiInline label="MTPD" value="4h" accent />
                <KpiInline label="RTO" value="2h" />
                <KpiInline label="Compliance" value="98%" />
                <div className="flex items-center gap-1">
                  <Award className="w-3 h-3 text-amber-400" />
                  <span className="text-[9px] font-extrabold text-amber-300 tracking-wide whitespace-nowrap">ISO 22301 ✓</span>
                </div>
              </div>

              {/* Inline keyframes */}
              <style>{`
                @keyframes pulseGlow {
                  0%, 100% { box-shadow: 0 0 60px rgba(16,185,129,0.55), inset 0 0 24px rgba(255,255,255,0.18); }
                  50%      { box-shadow: 0 0 90px rgba(16,185,129,0.8), inset 0 0 30px rgba(255,255,255,0.28); }
                }
                @keyframes float {
                  0%, 100% { transform: translateY(0); }
                  50%      { transform: translateY(-6px); }
                }
                @keyframes spin {
                  from { transform: rotate(0deg); }
                  to   { transform: rotate(360deg); }
                }
                @keyframes spinReverse {
                  from { transform: rotate(360deg); }
                  to   { transform: rotate(0deg); }
                }
              `}</style>
            </div>
            {/* Floating badges */}
            <div className="hidden sm:block absolute -bottom-4 -left-4 bg-slate-800 border border-slate-700 rounded-xl px-3 sm:px-4 py-2 sm:py-3 shadow-xl">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 sm:w-3 sm:h-3 bg-emerald-500 rounded-full animate-pulse"></div>
                <span className="text-xs sm:text-sm font-medium text-slate-300">AI-Powered Autonomy</span>
              </div>
            </div>
            <div className="hidden sm:block absolute -top-4 -right-4 bg-slate-800 border border-slate-700 rounded-xl px-3 sm:px-4 py-2 sm:py-3 shadow-xl">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-3 h-3 sm:w-4 sm:h-4 text-emerald-500" />
                <span className="text-xs sm:text-sm font-medium text-slate-300">ISO-22301 Compliant</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------------- Sub-components ---------------- */
function InputPill({ icon: Icon, label, sub, color, delay }) {
  const colorMap = {
    emerald: "border-emerald-500/40 text-emerald-300",
    sky: "border-sky-500/40 text-sky-300",
    amber: "border-amber-500/40 text-amber-300",
    cyan: "border-cyan-500/40 text-cyan-300",
  };
  return (
    <div
      className={`bg-slate-800/85 border rounded-lg px-2 py-1.5 flex items-center gap-2 shadow-md backdrop-blur-sm ${colorMap[color]}`}
      style={{ animation: `float 5s ease-in-out infinite ${delay}` }}
    >
      <Icon className="w-3.5 h-3.5 flex-shrink-0" />
      <div className="leading-tight min-w-0">
        <div className="text-[10px] font-bold text-white truncate">{label}</div>
        <div className="text-[8.5px] text-slate-400 truncate">{sub}</div>
      </div>
    </div>
  );
}

function OutputCard({ icon: Icon, label, meta, certified, delay }) {
  return (
    <div
      className="relative bg-gradient-to-br from-slate-50 to-slate-200 border border-slate-300 rounded-lg px-2 py-1.5 flex items-center gap-2 shadow-xl"
      style={{ animation: `float 5s ease-in-out infinite ${delay}` }}
    >
      <Icon className="w-3.5 h-3.5 flex-shrink-0 text-slate-700" />
      <div className="leading-tight min-w-0 flex-1">
        <div className="text-[10px] font-bold text-slate-800 truncate">{label}</div>
        <div className="text-[8.5px] text-slate-500 truncate">{meta}</div>
      </div>
      {certified && (
        <div className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full flex items-center justify-center shadow-md" style={{ background: "radial-gradient(circle at 30% 30%, #fde047, #ca8a04 60%, #713f12 100%)" }}>
          <Award className="w-3 h-3 text-amber-900" />
        </div>
      )}
    </div>
  );
}

function KpiInline({ label, value, accent }) {
  return (
    <div className="flex items-baseline gap-1 flex-shrink-0">
      <span className={`text-[10px] font-extrabold tracking-tight ${accent ? 'text-cyan-300' : 'text-white'}`}>{value}</span>
      <span className="text-[8.5px] text-slate-400 uppercase tracking-wide">{label}</span>
    </div>
  );
}
